import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebase';
import { WatchlistData, WatchlistGroup, StockReference } from '../types';
import { logger } from '../utils/logger';

/**
 * 優化的關注清單服務
 * 使用輕量化的 StockReference 儲存架構
 */
export class WatchlistService {
  private static readonly COLLECTION_NAME = 'watchlists';

  /**
   * 取得帳戶的關注清單
   * 只支援新的 stockReferences 格式
   */
  static async getUserWatchlist(accountId: string): Promise<WatchlistData | null> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const watchlistDoc = await getDoc(watchlistRef);

      if (watchlistDoc.exists()) {
        const data = watchlistDoc.data();
        const result = {
          groups: data.groups || [],
          ungroupedStockReferences: data.ungroupedStockReferences || [],
          dashboardStocks: data.dashboardStocks || [], // 包含儀表板股票
        };
        
        return result;
      }

      // Create empty watchlist if doesn't exist
      await this.createEmptyWatchlist(accountId);
      return { groups: [], ungroupedStockReferences: [], dashboardStocks: [] };
    } catch (error) {
      logger.watchlist.error('Failed to get user watchlist', error);
      return null;
    }
  }

  /**
   * 為新帳戶創建空的關注清單
   * allowedUsers 由帳號文件同步(rules create 驗證成員身分)
   */
  static async createEmptyWatchlist(accountId: string): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);

      // 讀取帳戶文件取得 allowedUsers
      const accountSnap = await getDoc(doc(db, 'tradingAccounts', accountId));
      const allowedUsers: string[] = accountSnap.exists()
        ? (accountSnap.data().allowedUsers ?? [])
        : [];

      await setDoc(watchlistRef, {
        accountId,
        allowedUsers,
        groups: [],
        ungroupedStockReferences: [],
        dashboardStocks: [], // 初始化儀表板股票清單
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.watchlist.error('Failed to create empty watchlist', error);
      throw new Error('Failed to create watchlist');
    }
  }

  /**
   * 添加股票引用到未分組清單
   */
  static async addStockReference(accountId: string, stockReference: StockReference): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const watchlistDoc = await getDoc(watchlistRef);

      if (!watchlistDoc.exists()) {
        await this.createEmptyWatchlist(accountId);
      }

      // 檢查是否已存在
      const currentData = watchlistDoc.exists() ? watchlistDoc.data() : {};
      const ungroupedRefs = currentData?.ungroupedStockReferences || [];
      const exists = ungroupedRefs.some((ref: StockReference) => ref.symbol === stockReference.symbol);
      
      if (!exists) {
        await updateDoc(watchlistRef, {
          ungroupedStockReferences: arrayUnion({
            ...stockReference,
            addedAt: new Date()
          }),
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      logger.watchlist.error('Failed to add stock reference', error);
      throw new Error('Failed to add stock to watchlist');
    }
  }

  /**
   * 從未分組清單移除股票引用
   */
  static async removeStockReference(accountId: string, symbol: string): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const watchlistDoc = await getDoc(watchlistRef);
      
      if (!watchlistDoc.exists()) {
        return;
      }
      
      const currentData = watchlistDoc.data();
      const ungroupedRefs = (currentData?.ungroupedStockReferences || [])
        .filter((ref: StockReference) => ref.symbol !== symbol);
      
      await updateDoc(watchlistRef, {
        ungroupedStockReferences: ungroupedRefs,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.watchlist.error('Failed to remove stock reference', error);
      throw new Error('Failed to remove stock from watchlist');
    }
  }

  /**
   * 創建新分組
   */
  static async createGroup(
    accountId: string, 
    groupName: string, 
    color: string = '#1976d2'
  ): Promise<string> {
    try {
      const groupId = `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const newGroup: WatchlistGroup = {
        id: groupId,
        name: groupName,
        color,
        stockReferences: [],
        createdAt: new Date(),
      };

      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const watchlistDoc = await getDoc(watchlistRef);

      if (!watchlistDoc.exists()) {
        await this.createEmptyWatchlist(accountId);
      }

      const currentData = watchlistDoc.exists() ? watchlistDoc.data() : { groups: [] };
      const updatedGroups = [...(currentData.groups || []), newGroup];

      await updateDoc(watchlistRef, {
        groups: updatedGroups,
        updatedAt: serverTimestamp(),
      });

      return groupId;
    } catch (error) {
      logger.watchlist.error('Failed to create group', error);
      throw new Error('Failed to create group');
    }
  }

  /**
   * 更新分組資訊
   */
  static async updateGroup(
    accountId: string,
    groupId: string,
    updates: Partial<Pick<WatchlistGroup, 'name' | 'color'>>
  ): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const watchlistDoc = await getDoc(watchlistRef);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const currentData = watchlistDoc.data();
      const groups = currentData.groups || [];
      
      const updatedGroups = groups.map((group: WatchlistGroup) =>
        group.id === groupId ? { ...group, ...updates } : group
      );

      await updateDoc(watchlistRef, {
        groups: updatedGroups,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.watchlist.error('Failed to update group', error);
      throw new Error('Failed to update group');
    }
  }

  /**
   * 更新分組名稱（便利方法）
   */
  static async updateGroupName(
    accountId: string,
    groupId: string,
    newName: string
  ): Promise<void> {
    return this.updateGroup(accountId, groupId, { name: newName });
  }

  /**
   * 刪除分組（股票引用移到未分組）
   */
  static async deleteGroup(accountId: string, groupId: string): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const watchlistDoc = await getDoc(watchlistRef);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const currentData = watchlistDoc.data();
      const groups = currentData.groups || [];
      
      // 找到要刪除的分組並將其股票引用移到未分組
      const groupToDelete = groups.find((group: WatchlistGroup) => group.id === groupId);
      const remainingGroups = groups.filter((group: WatchlistGroup) => group.id !== groupId);
      
      let updatedUngroupedRefs = currentData.ungroupedStockReferences || [];
      if (groupToDelete && (groupToDelete.stockReferences || []).length > 0) {
        updatedUngroupedRefs = [...updatedUngroupedRefs, ...(groupToDelete.stockReferences || [])];
      }

      await updateDoc(watchlistRef, {
        groups: remainingGroups,
        ungroupedStockReferences: updatedUngroupedRefs,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.watchlist.error('Failed to delete group', error);
      throw new Error('Failed to delete group');
    }
  }

  /**
   * 添加股票引用到指定分組
   */
  static async addStockReferenceToGroup(
    accountId: string, 
    groupId: string, 
    stockReference: StockReference
  ): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const watchlistDoc = await getDoc(watchlistRef);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const currentData = watchlistDoc.data();
      const groups = currentData.groups || [];
      
      const updatedGroups = groups.map((group: WatchlistGroup) => {
        if (group.id === groupId) {
          // 檢查股票是否已在分組中
          const stockExists = (group.stockReferences || []).some(ref => ref.symbol === stockReference.symbol);
          if (!stockExists) {
            return { 
              ...group, 
              stockReferences: [...(group.stockReferences || []), {
                ...stockReference,
                addedAt: new Date()
              }] 
            };
          }
        }
        return group;
      });

      // 同時從未分組移除（如果存在）
      const updatedUngroupedRefs = (currentData.ungroupedStockReferences || [])
        .filter((ref: StockReference) => ref.symbol !== stockReference.symbol);

      await updateDoc(watchlistRef, {
        groups: updatedGroups,
        ungroupedStockReferences: updatedUngroupedRefs,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.watchlist.error('Failed to add stock reference to group', error);
      throw new Error('Failed to add stock to group');
    }
  }

  /**
   * 從指定分組移除股票引用
   */
  static async removeStockReferenceFromGroup(
    accountId: string,
    groupId: string,
    symbol: string
  ): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const watchlistDoc = await getDoc(watchlistRef);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const currentData = watchlistDoc.data();
      const groups = currentData.groups || [];
      
      const updatedGroups = groups.map((group: WatchlistGroup) => {
        if (group.id === groupId) {
          return {
            ...group,
            stockReferences: (group.stockReferences || []).filter(ref => ref.symbol !== symbol),
          };
        }
        return group;
      });

      await updateDoc(watchlistRef, {
        groups: updatedGroups,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.watchlist.error('Failed to remove stock reference from group', error);
      throw new Error('Failed to remove stock from group');
    }
  }

  /**
   * 在分組間移動股票引用
   */
  static async moveStockReference(
    accountId: string,
    stockReference: StockReference,
    fromGroupId: string | null,
    toGroupId: string | null
  ): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const watchlistDoc = await getDoc(watchlistRef);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const currentData = watchlistDoc.data();
      let groups = currentData.groups || [];
      let ungroupedRefs = currentData.ungroupedStockReferences || [];

      // 從來源移除
      if (fromGroupId === null) {
        // 從未分組移除
        ungroupedRefs = ungroupedRefs.filter((ref: StockReference) => ref.symbol !== stockReference.symbol);
      } else {
        // 從指定分組移除
        groups = groups.map((group: WatchlistGroup) => {
          if (group.id === fromGroupId) {
            return {
              ...group,
              stockReferences: (group.stockReferences || []).filter(ref => ref.symbol !== stockReference.symbol),
            };
          }
          return group;
        });
      }

      // 添加到目標
      if (toGroupId === null) {
        // 添加到未分組
        const stockExists = ungroupedRefs.some((ref: StockReference) => ref.symbol === stockReference.symbol);
        if (!stockExists) {
          ungroupedRefs.push({
            ...stockReference,
            addedAt: new Date()
          });
        }
      } else {
        // 添加到指定分組
        groups = groups.map((group: WatchlistGroup) => {
          if (group.id === toGroupId) {
            const stockExists = (group.stockReferences || []).some(ref => ref.symbol === stockReference.symbol);
            if (!stockExists) {
              return { 
                ...group, 
                stockReferences: [...(group.stockReferences || []), {
                  ...stockReference,
                  addedAt: new Date()
                }] 
              };
            }
          }
          return group;
        });
      }

      await updateDoc(watchlistRef, {
        groups,
        ungroupedStockReferences: ungroupedRefs,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.watchlist.error('Failed to move stock reference', error);
      throw new Error('Failed to move stock');
    }
  }

  /**
   * 即時監聽關注清單變更
   */
  static subscribeToWatchlist(
    accountId: string,
    callback: (watchlistData: WatchlistData | null) => void
  ): () => void {
    const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);

    return onSnapshot(watchlistRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();

        callback({
          groups: data.groups || [],
          ungroupedStockReferences: data.ungroupedStockReferences || [],
          dashboardStocks: data.dashboardStocks || [],
        });
      } else {
        callback({ groups: [], ungroupedStockReferences: [], dashboardStocks: [] });
      }
    }, (error) => {
      // 監聽錯誤保留最後已知狀態,不回呼空清單
      logger.watchlist.error('Watchlist subscription error', error);
    });
  }

  /**
   * 檢查股票是否已在關注清單中
   */
  static async isStockInWatchlist(accountId: string, stockSymbol: string): Promise<boolean> {
    try {
      const watchlistData = await this.getUserWatchlist(accountId);
      
      if (!watchlistData) return false;

      // 檢查未分組
      const inUngrouped = (watchlistData.ungroupedStockReferences || []).some(ref => ref.symbol === stockSymbol);
      if (inUngrouped) return true;

      // 檢查分組
      const inGroups = watchlistData.groups.some(group =>
        (group.stockReferences || []).some(ref => ref.symbol === stockSymbol)
      );

      return inGroups;
    } catch (error) {
      logger.watchlist.error('Failed to check stock in watchlist', error);
      return false;
    }
  }

  /**
   * 取得關注清單中所有股票代號
   */
  static async getAllWatchlistSymbols(accountId: string): Promise<string[]> {
    try {
      const watchlistData = await this.getUserWatchlist(accountId);
      
      if (!watchlistData) return [];

      const symbols: string[] = [...(watchlistData.ungroupedStockReferences || []).map(ref => ref.symbol)];
      
      watchlistData.groups.forEach(group => {
        symbols.push(...(group.stockReferences || []).map(ref => ref.symbol));
      });

      // 去除重複
      return Array.from(new Set(symbols));
    } catch (error) {
      logger.watchlist.error('Failed to get all watchlist symbols', error);
      return [];
    }
  }

  /**
   * 設定分組是否在儀表板顯示
   */
  static async setGroupDashboardDisplay(accountId: string, groupId: string, displayOnDashboard: boolean): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const docSnap = await getDoc(watchlistRef);
      
      if (!docSnap.exists()) {
        throw new Error('Watchlist not found');
      }

      const data = docSnap.data();
      const groups = data.groups || [];
      
      const updatedGroups = groups.map((group: WatchlistGroup) => 
        group.id === groupId 
          ? { ...group, displayOnDashboard }
          : group
      );

      await updateDoc(watchlistRef, {
        groups: updatedGroups,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.watchlist.error('Failed to set group dashboard display', error);
      throw new Error('Failed to update dashboard display setting');
    }
  }

  /**
   * 取得要在儀表板顯示的分組
   */
  static async getDashboardWatchlistGroups(accountId: string): Promise<WatchlistGroup[]> {
    try {
      const watchlistData = await this.getUserWatchlist(accountId);
      
      if (!watchlistData) return [];

      // 返回設定為顯示在儀表板的分組，如果沒有設定則預設不顯示
      return watchlistData.groups.filter(group => group.displayOnDashboard === true);
    } catch (error) {
      logger.watchlist.error('Failed to get dashboard watchlist groups', error);
      return [];
    }
  }

  /**
   * 添加股票到儀表板顯示清單
   */
  static async addDashboardStock(accountId: string, stockReference: StockReference): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const docSnap = await getDoc(watchlistRef);
      
      if (!docSnap.exists()) {
        // 如果關注清單不存在，創建一個新的
        const accountSnap = await getDoc(doc(db, 'tradingAccounts', accountId));
        const allowedUsers: string[] = accountSnap.exists()
          ? (accountSnap.data().allowedUsers ?? [])
          : [];

        await setDoc(watchlistRef, {
          accountId,
          allowedUsers,
          groups: [],
          ungroupedStockReferences: [],
          dashboardStocks: [{
            ...stockReference,
            addedAt: new Date()
          }],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        return;
      }

      const data = docSnap.data();
      const dashboardStocks = data.dashboardStocks || [];
      
      // 檢查股票是否已經在儀表板清單中
      const stockExists = dashboardStocks.some((stock: StockReference) => stock.symbol === stockReference.symbol);
      if (stockExists) {
        logger.watchlist.warn('Stock already exists in dashboard', { symbol: stockReference.symbol });
        return;
      }

      // 添加股票到儀表板清單
      const updatedDashboardStocks = [
        ...dashboardStocks,
        {
          ...stockReference,
          addedAt: new Date()
        }
      ];

      await updateDoc(watchlistRef, {
        dashboardStocks: updatedDashboardStocks,
        updatedAt: serverTimestamp(),
      });
      
    } catch (error) {
      logger.watchlist.error('Failed to add dashboard stock', error);
      logger.watchlist.error('Failed to add dashboard stock', error);
      throw new Error('Failed to add stock to dashboard');
    }
  }

  /**
   * 從儀表板顯示清單移除股票
   */
  static async removeDashboardStock(accountId: string, stockSymbol: string): Promise<void> {
    try {
      const watchlistRef = doc(db, this.COLLECTION_NAME, accountId);
      const docSnap = await getDoc(watchlistRef);
      
      if (!docSnap.exists()) {
        logger.watchlist.warn('Watchlist not found for dashboard stock removal', { accountId });
        return;
      }

      const data = docSnap.data();
      const dashboardStocks = data.dashboardStocks || [];
      
      // 移除指定的股票
      const updatedDashboardStocks = dashboardStocks.filter((stock: StockReference) => stock.symbol !== stockSymbol);

      await updateDoc(watchlistRef, {
        dashboardStocks: updatedDashboardStocks,
        updatedAt: serverTimestamp(),
      });
      
    } catch (error) {
      logger.watchlist.error('Failed to remove dashboard stock', error);
      logger.watchlist.error('Failed to remove dashboard stock', error);
      throw new Error('Failed to remove stock from dashboard');
    }
  }

  /**
   * 取得儀表板顯示的股票清單
   */
  static async getDashboardStocks(accountId: string): Promise<StockReference[]> {
    try {
      const watchlistData = await this.getUserWatchlist(accountId);
      
      if (!watchlistData) return [];

      return watchlistData.dashboardStocks || [];
    } catch (error) {
      logger.watchlist.error('Failed to get dashboard stocks', error);
      return [];
    }
  }

  /**
   * 檢查股票是否在儀表板顯示清單中
   */
  static async isStockInDashboard(accountId: string, stockSymbol: string): Promise<boolean> {
    try {
      const dashboardStocks = await this.getDashboardStocks(accountId);
      return dashboardStocks.some(stock => stock.symbol === stockSymbol);
    } catch (error) {
      logger.watchlist.error('Failed to check stock in dashboard', error);
      return false;
    }
  }

}
